import { useMemo, useState, useContext} from 'react';
import Money from '@entities/Money';
import { Context } from '@entities/Context';
import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faReply, faPlusCircle, faCheckCircle, faCircleNotch, faAngleDown, faDollarSign, faChurch, faGlobeAmericas, faHandHolding, faTrash } from '@fortawesome/free-solid-svg-icons'
import { flattenDeep, values, trim } from "lodash"

// Error component
const ErrorMessage = ({ message }) => {
  if (!message) return null;
  
  return (
    <div className="px-4 py-3 mb-4 text-red-700 bg-red-100 border border-red-400 rounded">
      {message}
    </div>
  );
};

export default function ContributionForm() {
    const navigate = useNavigate()
    const { api, storage } = useContext(Context)
    const account = storage.get('account')
    const contributionDefaults = account?.metadata?.contribution_defaults || {}

    const defaultAmount = (value) => {
        const amount = Number(value || 0) / 100

        return amount > 0 ? amount.toFixed(2) : undefined
    }

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [tithe, setTithe] = useState(defaultAmount(contributionDefaults.tithe));
    const [missions, setMissions] = useState(defaultAmount(contributionDefaults.missions));
    const [building, setBuilding] = useState(defaultAmount(contributionDefaults.building));
    const [items, setItems] = useState([]);

    const sources = useMemo(() => storage.get('sources'), [])
    const [source, setSource] = useState(sources.length -1);

    const numberValue = (value) => {
        const str = typeof value === 'string' ? value.replace(/[^0-9.]+/g, '') : String(value);
        return parseFloat(str) || 0;
    };

    const money = (amount, decimals = 0, format) => { return new Money(amount, 100).display(decimals, format) }

    const handleBack = () => { navigate('/dashboard') }

    const handleQuickAmount = (field, amount) => {
        switch(field) {
            case 'tithe': setTithe(amount); break;
            case 'missions': setMissions(amount); break;
            case 'building': setBuilding(amount); break;
        }
    }

    const addItem = () => {
        setItems([...items, { description: '', amount: null }]);
    }

    const removeItem = (index) => {
        setItems(items.filter((_, i) => i !== index));
    }

    const updateItem = (index, field, value) => {
        const newItems = [...items];
        newItems[index] = { ...newItems[index], [field]: value };
        setItems(newItems);
    }

    const specialTotal = items.reduce((sum, item) => sum + numberValue(item.amount), 0);
    
    const total = money(
        numberValue(tithe) + 
        numberValue(missions) + 
        numberValue(building) + 
        specialTotal,
        2
    );

    const disabled = loading || numberValue(total) <= 0;

    const pay = async () => {
        if (loading) return;
        setLoading(true);
        setError('');

        let paymentItems = [
            { description: 'Tithe', amount: numberValue(tithe) },
            { description: 'Missions', amount: numberValue(missions) },
            { description: 'Building', amount: numberValue(building) },
            ...items
        ].filter(item => numberValue(item.amount) > 0)
        .map(item => ({
            description: item.description || 'Special Offering',
            amount: numberValue(item.amount)
        }));

        api.post('/api/payment', {
            account: account.id,
            pin: storage.get('pin'),
            items: paymentItems,
            source: storage.get('sources').at(source).id
        })
        .then(({ data }) => {
            storage.set('account', {
                ...account,
                metadata: {
                    ...(account.metadata || {}),
                    contribution_defaults: {
                        tithe: Math.round(numberValue(tithe) * 100),
                        missions: Math.round(numberValue(missions) * 100),
                        building: Math.round(numberValue(building) * 100),
                    },
                },
            })
            storage.get('payments').push(data.data)
            handleBack()
        })
        .catch((error) => {
            setError(trim(
                error.response.data.message+ ' ' + flattenDeep(values(error?.response?.data?.errors || {})).join(' ')
            ))
        })
        .finally(() => {
            setLoading(false)
        })
    };

    const handleSourceChange = (e) => {
        const value = e.target.value;
        if (value === '') {
            navigate('/source')
        } else {
            setSource(value);
        }
    };

    return (
        <div className="max-w-6xl p-6 mx-auto">
            <p className="mb-6 text-lg font-light">
                Use the form below to make a new contribution using your preferred pay source
                and desired contribution items. For love offerings or other designated
                gifts, add a "Special Offering" contribution.
            </p>

            <ErrorMessage message={error} />

            <div className="flex flex-wrap mb-4 -mx-4">
                
                <div className="w-full px-4 mb-4 sm:w-1/2 md:w-1/3">
                    <label className="block mb-2 font-bold text-center text-gray-700" htmlFor="tithe">
                        <span className="block mb-2">
                            <FontAwesomeIcon icon={faHandHolding} className="text-blue-500 fas fa-2x" />
                        </span>
                        Tithes
                    </label>

                    <div className="flex items-center w-full px-3 text-gray-700 border rounded shadow focus-within:shadow-outline">
                        <FontAwesomeIcon icon={faDollarSign} className="mr-3 text-gray-500 fas" />
                        <input
                        value={tithe}
                        onChange={(e) => setTithe(e.target.value)}
                        className="flex-1 w-full py-3 leading-tight text-right bg-transparent appearance-none focus:outline-none"
                        id="tithe"
                        type="text"
                        placeholder="0.00"
                        inputMode="decimal"
                        />
                    </div>

                    <div className="flex justify-between mt-2">
                        {[10, 25, 50, 100].map(amount => (
                        <span
                            key={amount}
                            onClick={() => handleQuickAmount('tithe', amount)}
                            className="px-3 py-1 text-xs font-bold text-white bg-blue-300 rounded hover:bg-blue-500 hover:cursor-pointer"
                        >
                            {money(amount)}
                        </span>
                        ))}
                    </div>
                </div>

                {/* Missions */}
                <div className="w-full px-4 mb-4 sm:w-1/2 md:w-1/3">
                    <label className="block mb-2 font-bold text-center text-gray-700" htmlFor="missions">
                        <span className="block mb-2">
                            <FontAwesomeIcon icon={faGlobeAmericas} className="text-blue-500 fas fa-2x" />
                        </span>
                        Missions
                    </label>

                    <div className="flex items-center w-full px-3 text-gray-700 border rounded shadow focus-within:shadow-outline">
                        <FontAwesomeIcon icon={faDollarSign} className="mr-3 text-gray-500 fas" />
                        <input
                        value={missions}
                        onChange={(e) => setMissions(e.target.value)}
                        className="flex-1 w-full py-3 leading-tight text-right bg-transparent appearance-none focus:outline-none"
                        id="missions"
                        type="text"
                        placeholder="0.00"
                        inputMode="decimal"
                        />
                    </div>

                    <div className="flex justify-between mt-2">
                        {[10, 25, 50, 100].map(amount => (
                        <span
                            key={amount}
                            onClick={() => handleQuickAmount('missions', amount)}
                            className="px-3 py-1 text-xs font-bold text-white bg-blue-300 rounded hover:bg-blue-500 hover:cursor-pointer"
                        >
                            {money(amount)}
                        </span>
                        ))}
                    </div>
                </div>

                {/* Building Fund */}
                <div className="w-full px-4 mb-4 sm:w-1/2 md:w-1/3">
                    <label className="block mb-2 font-bold text-center text-gray-700" htmlFor="building">
                        <span className="block mb-2">
                            <FontAwesomeIcon icon={faChurch} className="mr-3 text-blue-500 fa-2x fas" />
                        </span>
                        Building Fund
                    </label>

                    <div className="flex items-center w-full px-3 text-gray-700 border rounded shadow focus-within:shadow-outline">
                        <FontAwesomeIcon icon={faDollarSign} className="mr-3 text-gray-500 fas" />
                        <input
                        value={building}
                        onChange={(e) => setBuilding(e.target.value)}
                        className="flex-1 w-full py-3 leading-tight text-right bg-transparent appearance-none focus:outline-none"
                        id="building"
                        type="text"
                        placeholder="0.00"
                        inputMode="decimal"
                        />
                    </div>

                    <div className="flex justify-around mt-2">
                        {[10, 25, 50, 100].map(amount => (
                        <span
                            key={amount}
                            onClick={() => handleQuickAmount('building', amount)}
                            className="px-3 py-1 text-xs font-bold text-white bg-blue-300 rounded hover:bg-blue-500 hover:cursor-pointer"
                        >
                            {money(amount)}
                        </span>
                        ))}
                    </div>
                </div>
            </div>

            {/* Special Offerings */}
            {items.map((item, index) => (
                <div key={index} className="flex flex-wrap -mx-4">
                    <div className="w-full px-4 mb-4 sm:w-1/2 md:w-2/3">
                        <label className="block mb-2 font-bold text-left text-gray-700" htmlFor={`description_${index}`}>
                            Offering Description
                        </label>
                        <input
                            value={item.description}
                            onChange={(e) => updateItem(index, 'description', e.target.value)}
                            className="w-full px-3 py-3 leading-tight text-gray-700 border rounded shadow appearance-none focus:outline-none focus:shadow-outline"
                            id={`description_${index}`}
                            type="text"
                            maxLength="64"
                            placeholder="e.g.: Children's Ministry"
                        />
                    </div>
                    <div className="w-full px-4 mb-4 sm:w-1/2 md:w-1/3">
                        <label className="block mb-2 font-bold text-left text-gray-700" htmlFor={`amount_${index}`}>
                            Amount
                        </label>
                        <div className="flex">
                            <div className="flex items-center w-full px-3 text-gray-700 border rounded shadow focus-within:shadow-outline">
                                <FontAwesomeIcon icon={faDollarSign} className="mr-3 text-gray-500 fas" />
                                <input
                                    value={item.amount}
                                    onChange={(e) => updateItem(index, 'amount', e.target.value)}
                                    className="flex-1 w-full py-3 leading-tight text-right bg-transparent appearance-none focus:outline-none"
                                    id={`amount_${index}`}
                                    type="text"
                                    placeholder="0.00"
                                    inputMode="decimal"
                                />
                            </div>
                            <button
                                type="button"
                                onClick={() => removeItem(index)}
                                className="pl-5 text-gray-500 focus:outline-none hover:text-red-500 hover:cursor-pointer"
                            >
                                <FontAwesomeIcon icon={faTrash} className="fas" />
                            </button>
                        </div>
                    </div>
                </div>
            ))}

            <div className="flex flex-wrap mt-2 -mx-4">
                <div className="w-full px-4 md:w-1/2">
                <button
                    onClick={addItem}
                    className="px-4 py-2 mb-6 text-sm font-bold text-white rounded bg-primary"
                    type="button"
                >
                    <FontAwesomeIcon icon={faPlusCircle} className="mr-3 fas"/>
                    Special Offering
                </button>
                <br />
                    <button onClick={handleBack} className="text-gray-500 truncate hover:text-primary">
                        <FontAwesomeIcon icon={faReply} className="mr-2 fas" />
                        Dashboard
                    </button>
                </div>

                <div className="flex flex-col w-full px-4 pt-2 md:w-1/2">
                <div className="px-4 py-4 mb-6 truncate bg-gray-100 rounded">
                    <h2 className="mb-4 text-3xl leading-tight text-center">
                        <small className="block text-sm">Contribution Total</small>
                        <strong className="font-bold">{total}</strong>
                    </h2>
                    <label className="block mb-2 font-bold text-left text-gray-700" htmlFor="source">
                        Pay Using
                    </label>
                    <div className="relative">
                        <select
                            value={source}
                            onChange={handleSourceChange}
                            className="w-full px-3 py-3 leading-tight text-gray-700 bg-white border rounded shadow appearance-none focus:outline-none focus:shadow-outline"
                            id="source"
                            required
                        >
                            {sources.map(s => (
                            <option key={s.id} value={s.id}>
                                {s.description ? `${s.description} (${s.brand} ****${s.last4})` : `${s.brand} ****${s.last4}`}
                            </option>
                            ))}
                            <option disabled>{'─'.repeat(30)}</option>
                            <option value="">+ Add Card</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center px-2 my-1 mr-1 text-gray-700 bg-white rounded pointer-events-none">
                            <FontAwesomeIcon icon={faAngleDown} className="mr-2 fas" />
                        </div>
                    </div>
                </div>

                <button
                    onClick={pay}
                    disabled={disabled}
                    className="px-6 py-3 font-bold text-white rounded bg-primary disabled:bg-blue-400 disabled:cursor-not-allowed"
                    type="button"
                >
                    <span className={loading ? '' : 'hidden'}>
                        <FontAwesomeIcon icon={faCircleNotch} className="mr-2 fas animate-spin" />
                    </span>
                    <span className={loading ? 'hidden' : ''}>
                        <FontAwesomeIcon icon={faCheckCircle} className="mr-2 fas" />
                    </span>
                    <span className="inline-block ml-3">{loading ? 'Processing...' : 'Pay Now'}</span>
                </button>
                </div>
            </div>
        </div>
    );
}
